/**
 * Реестр Tabler Icons для динамического рендеринга по имени из CMS.
 *
 * НЕ используйте `import * as TbIcons` — это тянет все ~5000 иконок (~2.8 MB).
 * Добавляйте новые иконки в нужную группу: импорт + ICON_MAP.
 */

// ─── Sanity CMS: туры и активности ──────────────────────────────────────────
import {
    TbBackpack,
    TbBeach,
    TbCameraPin,
    TbCheese,
    TbChefHat,
    TbChristmasTree,
    TbCompass,
    TbRun,
    TbSailboat,
    TbSnowboarding,
    TbTrain,
    TbTrees,
    TbWalk,
} from 'react-icons/tb';

// ─── Sanity CMS: размещение ─────────────────────────────────────────────────
import {
    TbBed,
    TbBuilding,
    TbBuildingCastle,
    TbBuildingCommunity,
    TbBuildingFortress,
    TbBuildings,
    TbHome,
    TbHomeEco,
} from 'react-icons/tb';

// ─── Транспорт ──────────────────────────────────────────────────────────────
import {
    TbBus,
    TbCar,
    TbCarSuv,
    TbMotorbike,
    TbPlane,
    TbPlaneInflight,
    TbRoad,
    TbRoute,
    TbBuildingAirport,
} from 'react-icons/tb';

// ─── Безопасность и страхование ─────────────────────────────────────────────
import {
    TbAmbulance,
    TbHeart,
    TbMedicineSyrup,
    TbShield,
    TbShieldCheck,
    TbShieldHalfFilled,
    TbShieldPlus,
    TbStethoscope,
} from 'react-icons/tb';

// ─── Оплата ─────────────────────────────────────────────────────────────────
import {
    TbBrandAlipay,
    TbBrandPaypal,
    TbBrandStripe,
    TbCreditCard,
    TbCreditCardFilled,
    TbCurrencyBitcoin,
    TbDeviceMobileDollar,
} from 'react-icons/tb';

// ─── Бренды и соцсети ───────────────────────────────────────────────────────
import {
    TbBrandBooking,
    TbBrandInstagram,
    TbBrandMessenger,
    TbBrandPinterest,
    TbBrandTelegram,
    TbBrandTiktok,
    TbBrandWhatsapp,
    TbBrandX,
} from 'react-icons/tb';

// ─── UI / общие ─────────────────────────────────────────────────────────────
import {
    TbAlertCircle,
    TbArrowRight,
    TbBook,
    TbBriefcase2,
    TbBulb,
    TbCheck,
    TbChevronRight,
    TbClock,
    TbCopy,
    TbDeviceSim,
    TbDeviceTvOld,
    TbDownload,
    TbFileCheck,
    TbHeadset,
    TbKey,
    TbLayoutGrid,
    TbLocation,
    TbMail,
    TbMailForward,
    TbMapPin,
    TbMoodSmile,
    TbPlugConnected,
    TbRefresh,
    TbSearch,
    TbSend,
    TbServer2,
    TbStarFilled,
    TbUser,
    TbWorld,
    TbX,
} from 'react-icons/tb';

// ─── Маппинг ────────────────────────────────────────────────────────────────

const ICON_MAP = {
    // Sanity CMS: туры и активности
    TbBackpack,
    TbBeach,
    TbCameraPin,
    TbCheese,
    TbChefHat,
    TbChristmasTree,
    TbCompass,
    TbRun,
    TbSailboat,
    TbSnowboarding,
    TbTrain,
    TbTrees,
    TbWalk,

    // Sanity CMS: размещение
    TbBed,
    TbBuilding,
    TbBuildingCastle,
    TbBuildingCommunity,
    TbBuildingFortress,
    TbBuildings,
    TbHome,
    TbHomeEco,

    // Транспорт
    TbBuildingAirport,
    TbBus,
    TbCar,
    TbCarSuv,
    TbMotorbike,
    TbPlane,
    TbPlaneInflight,
    TbRoad,
    TbRoute,

    // Безопасность и страхование
    TbAmbulance,
    TbHeart,
    TbMedicineSyrup,
    TbShield,
    TbShieldCheck,
    TbShieldHalfFilled,
    TbShieldPlus,
    TbStethoscope,

    // Оплата
    TbBrandAlipay,
    TbBrandPaypal,
    TbBrandStripe,
    TbCreditCard,
    TbCreditCardFilled,
    TbCurrencyBitcoin,
    TbDeviceMobileDollar,

    // Бренды и соцсети
    TbBrandBooking,
    TbBrandInstagram,
    TbBrandMessenger,
    TbBrandPinterest,
    TbBrandTelegram,
    TbBrandTiktok,
    TbBrandWhatsapp,
    TbBrandX,

    // UI / общие
    TbAlertCircle,
    TbArrowRight,
    TbBook,
    TbBriefcase2,
    TbBulb,
    TbCheck,
    TbChevronRight,
    TbClock,
    TbCopy,
    TbDeviceSim,
    TbDeviceTvOld,
    TbDownload,
    TbFileCheck,
    TbHeadset,
    TbKey,
    TbLayoutGrid,
    TbLocation,
    TbMail,
    TbMailForward,
    TbMapPin,
    TbMoodSmile,
    TbPlugConnected,
    TbRefresh,
    TbSearch,
    TbSend,
    TbServer2,
    TbStarFilled,
    TbUser,
    TbWorld,
    TbX,
};

/**
 * Рендерит иконку из Tabler Icons по названию
 * @param {string} iconName - Название иконки (например: 'TbWalk', 'TbHome')
 * @param {object} props - Пропсы для иконки (size, className и т.д.)
 * @returns {React.Component|null} - Компонент иконки или null если не найдена
 */
export function renderTablerIcon(iconName, props = {}) {
    if (!iconName || typeof iconName !== 'string') {
        return null;
    }

    const normalizedName = iconName.trim();
    const IconComponent = ICON_MAP[normalizedName];

    if (!IconComponent) {
        console.warn(`Icon "${iconName}" not found in ICON_MAP. Add it to src/utils/iconRenderer.js`);
        return null;
    }

    return <IconComponent size={40} className="" {...props} />;
}

/**
 * Рендерит иконку с обёрткой для карточек
 * @param {string} iconName - Название иконки
 * @param {object} options - Опции для рендеринга
 * @returns {React.Component|null}
 */
export function renderIconCard(iconName, options = {}) {
    const {
        size = 40,
        className = '',
        wrapperClassName = '',
        color = 'text-gray-700'
    } = options;

    const icon = renderTablerIcon(iconName, { size, className: `${color} ${className}` });

    if (!icon) {
        return null;
    }

    return (
        <div className={wrapperClassName}>
            {icon}
        </div>
    );
}
